import React, { useState } from 'react';
import { Plus, Search, Mail, Filter, Eye, Edit2, Copy, Trash2, MoreVertical, Clock, TrendingUp } from 'lucide-react';
import {
  useEmailTemplates,
  useDeleteEmailTemplate,
  useCloneEmailTemplate,
  getCategoryLabel,
  getCategoryColor,
  type EmailTemplate,
  type TemplateCategory
} from '../../../services/queries/emailTemplateQueries';
import { DevModeBanner } from '../../common/DevModeBanner';
import { CreateTemplateModal } from './CreateTemplateModal';
import { EditTemplateModal } from './EditTemplateModal';
import { TemplatePreviewModal } from './TemplatePreviewModal';
import { useToast } from '../../../contexts/ToastContext';
import {
  StandardButton,
  StandardSearchBar,
  StandardSelect,
  StandardLoadingState,
  StandardErrorState,
  StandardEmptyState
} from '../../standard';

export const EmailTemplatesHome: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<TemplateCategory | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [previewingTemplate, setPreviewingTemplate] = useState<EmailTemplate | null>(null);
  const [templateMenuOpen, setTemplateMenuOpen] = useState<string | null>(null);

  const { data: templates, isLoading, error } = useEmailTemplates({
    searchQuery,
    category: categoryFilter,
    isActive: statusFilter === 'all' ? undefined : statusFilter === 'active'
  });

  const deleteTemplateMutation = useDeleteEmailTemplate();
  const cloneTemplateMutation = useCloneEmailTemplate();
  const { success, error: showError, warning } = useToast();

  const totalTemplates = templates?.length || 0;
  const activeTemplates = templates?.filter(t => t.isActive).length || 0;
  const totalUsage = templates?.reduce((sum, t) => sum + t.usageCount, 0) || 0;

  const handleDelete = async (templateId: string, templateName: string, isSystem: boolean) => {
    // Prevent deletion of system templates
    if (isSystem) {
      warning('Cannot delete system template', 'System templates are essential for system operations');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${templateName}"?`)) return;

    try {
      await deleteTemplateMutation.mutateAsync(templateId);
      success('Template deleted successfully!');
      setTemplateMenuOpen(null);
    } catch (err: any) {
      showError('Failed to delete template', err.message || 'Please try again');
    }
  };

  const handleClone = async (templateId: string) => {
    try {
      const cloned = await cloneTemplateMutation.mutateAsync(templateId);
      setEditingTemplate(cloned);
      setTemplateMenuOpen(null);
    } catch (error) {
      console.error('Failed to clone template:', error);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Email Templates</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-1">
              Create and manage email templates with merge fields
            </p>
          </div>
          <StandardButton
            variant="primary"
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={() => setShowCreateModal(true)}
          >
            New Template
          </StandardButton>
        </div>

        {/* Development Mode Banner */}
        <DevModeBanner
          dataSource="mockDataManager.ts"
          itemCount={templates ? `${templates.length} email templates` : 'email templates'}
          customMessage={`Development Mode: ${templates ? `Showing ${templates.length} email templates` : 'Using mock data'} persisted via mockDataManager.ts (localStorage). Changes survive page refresh.`}
          dismissible={true}
        />

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {totalTemplates}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Total Templates</div>
              </div>
              <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                <Mail className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {activeTemplates}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Active Templates</div>
              </div>
              <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-lg">
                <Mail className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {totalUsage.toLocaleString()}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Total Sent</div>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <TrendingUp className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1">
            <StandardSearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search templates by name or subject..."
            />
          </div>

          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />

            <StandardSelect
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value as TemplateCategory | 'all')}
              options={[
                { value: 'all', label: 'All Categories' },
                { value: 'user', label: 'User' },
                { value: 'client', label: 'Client' },
                { value: 'case', label: 'Case' },
                { value: 'notification', label: 'Notification' },
                { value: 'system', label: 'System' },
                { value: 'custom', label: 'Custom' }
              ]}
            />

            <StandardSelect
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' }
              ]}
            />
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <StandardLoadingState message="Loading templates..." />
      )}

      {/* Error State */}
      {error && (
        <StandardErrorState
          title="Error loading templates"
          message="Please try again."
        />
      )}

      {/* Templates Grid */}
      {!isLoading && !error && (
        <>
          {templates && templates.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onPreview={() => setPreviewingTemplate(template)}
                  onEdit={() => setEditingTemplate(template)}
                  onClone={() => handleClone(template.id)}
                  onDelete={() => handleDelete(template.id, template.name, template.isSystem)}
                  menuOpen={templateMenuOpen === template.id}
                  onMenuToggle={() => setTemplateMenuOpen(templateMenuOpen === template.id ? null : template.id)}
                />
              ))}
            </div>
          ) : (
            <StandardEmptyState
              icon={<Mail className="w-16 h-16" />}
              title={searchQuery || categoryFilter !== 'all' || statusFilter !== 'all' ? 'No templates found' : 'No email templates yet'}
              message={searchQuery || categoryFilter !== 'all' || statusFilter !== 'all'
                ? 'Try adjusting your search or filter criteria'
                : 'Create your first email template to get started'
              }
              actionLabel={searchQuery || categoryFilter !== 'all' || statusFilter !== 'all' ? 'Clear filters' : undefined}
              onAction={searchQuery || categoryFilter !== 'all' || statusFilter !== 'all'
                ? () => {
                    setSearchQuery('');
                    setCategoryFilter('all');
                    setStatusFilter('all');
                  }
                : undefined
              }
            />
          )}
        </>
      )}

      {/* Modals */}
      <CreateTemplateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />

      <EditTemplateModal
        template={editingTemplate}
        isOpen={!!editingTemplate}
        onClose={() => setEditingTemplate(null)}
      />

      <TemplatePreviewModal
        template={previewingTemplate}
        isOpen={!!previewingTemplate}
        onClose={() => setPreviewingTemplate(null)}
      />
    </div>
  );
};

// Template Card Component
interface TemplateCardProps {
  template: EmailTemplate;
  onPreview: () => void;
  onEdit: () => void;
  onClone: () => void;
  onDelete: () => void;
  menuOpen: boolean;
  onMenuToggle: () => void;
}

const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  onPreview,
  onEdit,
  onClone,
  onDelete,
  menuOpen,
  onMenuToggle
}) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-shadow">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-white truncate mb-1">
              {template.name}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 truncate">
              {template.subject}
            </p>
          </div>

          <div className="relative ml-2">
            <button
              onClick={onMenuToggle}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            >
              <MoreVertical className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </button>

            {menuOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={onMenuToggle}
                />
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20">
                  <div className="py-1">
                    <button
                      onClick={onPreview}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      Preview
                    </button>
                    <button
                      onClick={onEdit}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={onClone}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <Copy className="w-4 h-4" />
                      Clone
                    </button>
                    {!template.isSystem && (
                      <>
                        <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                        <button
                          onClick={onDelete}
                          className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className={`px-2 py-0.5 text-xs font-medium rounded ${getCategoryColor(template.category)}`}>
            {getCategoryLabel(template.category)}
          </span>
          {template.isSystem && (
            <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded font-medium">
              System
            </span>
          )}
          {template.isActive ? (
            <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-xs rounded font-medium">
              Active
            </span>
          ) : (
            <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400 text-xs rounded font-medium">
              Inactive
            </span>
          )}
        </div>
      </div>

      {/* Body Preview */}
      <div className="p-4 bg-gray-50 dark:bg-gray-900">
        <div
          className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3"
          dangerouslySetInnerHTML={{ __html: template.body }}
        />
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <div className="flex items-center gap-1">
            <TrendingUp className="w-3 h-3" />
            <span>{template.usageCount} sent</span>
          </div>
          {template.lastUsed && (
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>{new Date(template.lastUsed).toLocaleDateString()}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

